import { Link } from "react-router-dom";
import { ArrowRight, Compass, Settings2, Rocket, LineChart } from "lucide-react";
import Seo from "@/components/Seo";

const PHASES = [
    {
        icon: Compass,
        step: "Phase 1",
        title: "Highlight",
        copy: "We identify where AI can create measurable business impact. This includes workflow mapping, bottleneck analysis and opportunity scoring.",
        outputs: ["Workflow map", "Bottleneck list", "AI opportunity matrix", "Prioritised use cases"],
    },
    {
        icon: Settings2,
        step: "Phase 2",
        title: "Engineer",
        copy: "We design simple, practical systems that solve real operational problems. Each system is built around triggers, process, output and human oversight.",
        outputs: ["System blueprints", "Implementation plan", "Risk considerations", "Team usage model"],
    },
    {
        icon: Rocket,
        step: "Phase 3",
        title: "Launch",
        copy: "We deploy systems into the business, test them, train the team and ensure they are usable in daily work.",
        outputs: ["Live systems", "Team training", "Usage guidance", "Initial performance review"],
    },
    {
        icon: LineChart,
        step: "Phase 4",
        title: "Manage",
        copy: "We optimise, report and expand over time. This is where AI becomes part of how the business operates.",
        outputs: ["Monthly reporting", "System improvements", "Adoption support", "Expansion roadmap"],
    },
];

export default function HelmFramework() {
    return (
        <div data-testid="page-helm">
            <Seo
                title="The Helm Framework | Chief AI Partners"
                description="The Helm Framework is our proprietary implementation model for moving AI from experimentation to execution across the mid-market."
                path="/helm-framework"
            />

            <section className="cap-section pt-24 md:pt-32 pb-16" data-testid="helm-hero">
                <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-8">
                        <div className="cap-eyebrow mb-6">The Helm Framework</div>
                        <h1 className="font-serif text-navy text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
                            A practical operating system for AI.
                        </h1>
                    </div>
                    <div className="lg:col-span-4 lg:pt-4">
                        <p className="text-slate text-lg leading-relaxed">
                            The Helm Framework (Highlight, Engineer, Launch, Manage) is our proprietary system for installing and running AI inside a business. It provides a clear path from identification to execution and ongoing optimisation.
                        </p>
                    </div>
                </div>
                <div className="cap-container mt-12"><div className="cap-rule" /></div>
            </section>

            <section className="cap-section" data-testid="helm-phases">
                <div className="cap-container space-y-16">
                    {PHASES.map(({ icon: Icon, step, title, copy, outputs }, i) => (
                        <div
                            key={title}
                            className="grid grid-cols-1 lg:grid-cols-12 gap-10 border-t border-sand pt-12 first:border-t-0 first:pt-0"
                            data-testid={`helm-phase-${i}`}
                        >
                            <div className="lg:col-span-5">
                                <div className="flex items-center gap-4 mb-4">
                                    <Icon className="text-navy" size={28} strokeWidth={1.4} />
                                    <span className="cap-eyebrow">{step}</span>
                                </div>
                                <h2 className="font-serif text-navy text-5xl md:text-6xl leading-[1.02] tracking-tight">
                                    {title}
                                </h2>
                            </div>
                            <div className="lg:col-span-7 space-y-8">
                                <p className="text-slate text-lg leading-relaxed max-w-2xl">
                                    {copy}
                                </p>
                                <div>
                                    <div className="cap-eyebrow mb-4 text-slate">Outputs</div>
                                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                                        {outputs.map((o) => (
                                            <li key={o} className="flex items-baseline gap-3 text-navy text-base border-b border-sand py-2.5">
                                                <span className="text-teal text-xs">&#9679;</span>
                                                <span>{o}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="cap-section bg-sand/40 border-y border-sand" data-testid="helm-final-cta">
                <div className="cap-container text-center max-w-3xl mx-auto">
                    <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] tracking-tight">
                        Ready to put the Helm Framework to work?
                    </h2>
                    <p className="mt-6 text-slate text-lg leading-relaxed">
                        Book a 15-minute conversation and we'll show you how the framework maps to your business.
                    </p>
                    <Link to="/contact" className="cap-btn-primary mt-8" data-testid="helm-cta-book">
                        Book a 15-minute conversation <ArrowRight size={16} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
